import React from 'react';
import { Capacitor } from '@capacitor/core';
import {
  reportChunkRecovery,
  shouldRecoverFromChunkFailure,
  triggerChunkReload,
} from '../../../utils/chunkErrorHandler';

interface NativeErrorBoundaryState {
  error: Error | null;
  recoveringChunk: boolean;
}

/**
 * Catch les erreurs de render (web + natif).
 * Sur web, renvoyer `children` après une erreur = écran blanc.
 * ChunkLoadError / HTML-as-JS → reload unique (comme le back-office).
 */
export class NativeErrorBoundary extends React.Component<
  { children: React.ReactNode },
  NativeErrorBoundaryState
> {
  state: NativeErrorBoundaryState = { error: null, recoveringChunk: false };

  static getDerivedStateFromError(error: Error): NativeErrorBoundaryState {
    if (shouldRecoverFromChunkFailure(error)) {
      return { error, recoveringChunk: true };
    }
    return { error, recoveringChunk: false };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    if (shouldRecoverFromChunkFailure(error)) {
      reportChunkRecovery('error_boundary');
      const reloading = triggerChunkReload();
      if (reloading) return;
      this.setState({ recoveringChunk: false });
    }
    console.error('[NativeErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (this.state.recoveringChunk && this.state.error) {
      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#faf6f3',
            color: '#666',
            fontFamily: 'Lexend Deca, sans-serif',
          }}
        >
          Mise à jour en cours…
        </div>
      );
    }

    if (!this.state.error) {
      return this.props.children;
    }

    const isNative = Capacitor.isNativePlatform();

    return (
      <div
        style={{
          minHeight: '100vh',
          padding: '24px',
          paddingTop: isNative ? 'calc(24px + env(safe-area-inset-top))' : 24,
          background: '#faf6f3',
          color: '#2c1810',
          fontFamily: 'Lexend Deca, sans-serif',
        }}
      >
        <h1 style={{ fontSize: '1.25rem', marginBottom: 12 }}>
          Une erreur est survenue
        </h1>
        <p style={{ marginBottom: 16, color: '#5c4033' }}>
          Rechargez la page. Si le problème continue, reconnectez-vous.
        </p>
        <button
          type='button'
          onClick={() => window.location.reload()}
          style={{
            marginBottom: 16,
            padding: '10px 16px',
            borderRadius: 10,
            border: 'none',
            background: '#a5765c',
            color: '#fff',
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Recharger la page
        </button>
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: 12,
            background: '#fff',
            padding: 12,
            borderRadius: 12,
            border: '1px solid rgba(165,118,92,0.2)',
          }}
        >
          {this.state.error.message}
        </pre>
      </div>
    );
  }
}
